import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Loader2, CheckCircle } from "lucide-react";
import Header from "../components/Header";
import { subscriberApi } from "../lib/api";

export default function SubscriberRegister() {
  const nav = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    
    setSubmitting(true);
    try {
      await subscriberApi.register({
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        password: password,
      });
      setSuccess(true);
      setTimeout(() => {
        nav("/login");
      }, 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="subscriber-register-success">
        <Header />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white border border-primary/15 rounded-sm shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
              <CheckCircle size={32} />
            </div>
            <h1 className="font-display text-2xl font-semibold text-primary mb-2">
              Compte créé avec succès !
            </h1>
            <p className="text-sm text-foreground/60 mb-6">
              Votre compte a été créé. Vous allez être redirigé vers la page de connexion...
            </p>
            <div className="text-xs text-foreground/50">
              Si vous n'êtes pas redirigé automatiquement,{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                cliquez ici pour vous connecter
              </Link>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="subscriber-register-page">
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-primary/15 rounded-sm shadow-lg p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
            / espace abonné
          </div>
          <h1 className="font-display text-2xl font-semibold text-primary mb-1">
            Créer un compte
          </h1>
          <p className="text-sm text-foreground/60 mb-6">
            Inscrivez-vous pour accéder à votre espace personnel et gérer votre abonnement.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <Field
              id="fullName"
              icon={User}
              label="Nom complet"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              testid="register-fullname-input"
              placeholder="Ex. Jean Dupont"
            />
            <Field
              id="email"
              icon={Mail}
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              testid="register-email-input"
              placeholder="Ex. jean.dupont@email.com"
            />
            <Field
              id="password"
              icon={Lock}
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testid="register-password-input"
              placeholder="Minimum 8 caractères"
            />
            <Field
              id="confirmPassword"
              icon={Lock}
              label="Confirmer le mot de passe"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              testid="register-confirm-password-input"
              placeholder="Répétez votre mot de passe"
            />

            {error && (
              <div className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
              data-testid="register-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              Créer mon compte
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-primary/10 text-center">
            <p className="text-xs text-foreground/50 mb-2">Déjà un compte ?</p>
            <Link
              to="/login"
              className="text-xs font-medium text-primary hover:underline"
            >
              Se connecter →
            </Link>
          </div>
          
          <div className="mt-4 text-center">
            <Link to="/pricing" className="text-xs text-foreground/60 hover:text-primary">
              Voir les offres d'abonnement →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ id, icon: Icon, label, testid, placeholder, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-2 border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">
        <Icon size={14} className="text-foreground/40" />
        <input
          id={id}
          required
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm py-2.5 outline-none"
          data-testid={testid}
          {...rest}
        />
      </div>
    </div>
  );
}
